 /**
  * @fileoverview Local-first tenant service for system-web department records.
  * @module system-web/services/tenantService
  *
  * Responsibilities:
  * - Read tenant records from Dexie for all department and trial UI surfaces.
  * - Apply local-first tenant mutations and orchestrate server-side lifecycle actions.
  * - Manage local-first trial extensions and activation.
  * - Provide a reactive liveQuery stream and on-demand server reconciliation for
  *   provisioning-state detection.
  *
  * Dependencies:
  * - @firesquirrel/local-db
  * - dexie (liveQuery)
  * - systemAuditLogService
  * - apiClient for inherently-online tenant lifecycle endpoints
  *
  * Architecture:
  * - Local-first. UI reads from Dexie; this service owns local cache updates and
  *   any required reconciliation with authoritative server routes.
  *
  * Notes:
  * - Trial removal is exposed here so department-level screens do not call the
  *   API directly.
  * - watchTenants() wraps listTenants in a Dexie liveQuery so pages can subscribe
  *   to reactive updates without polling.
  * - refreshTenantFromServer() is the on-demand reconciliation path for
  *   inherently-online operations like provisioning status detection.
  */
import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { liveQuery, type Observable } from 'dexie';
import { computeAuditChanges, writeAuditLog } from './systemAuditLogService';

function normalizeTenantIdentity(data: any): { slug: string | null; nerisId: string | null } {
  const slug = typeof data?.slug === 'string' && data.slug.trim() ? data.slug.trim().toLowerCase() : null;
  const nerisId = typeof data?.nerisId === 'string' && data.nerisId.trim() ? data.nerisId.trim().toUpperCase() : null;
  return { slug, nerisId };
}

function buildTenantIdentityConflictMessage(existing: any, field: 'slug' | 'nerisId', value: string): string {
  const existingName = typeof existing?.name === 'string' && existing.name.trim() ? ` "${existing.name}"` : '';
  return `A department${existingName} already uses ${field} "${value}". Department NERIS IDs and slugs must be unique.`;
}

/**
 * @process normalizeServerTenantForDexie
 *
 * Steps:
 * 1. Copy the server tenant document before local persistence.
 * 2. Convert omitted server lifecycle fields that were removed with MongoDB
 *    `$unset` into explicit null values.
 *
 * Inputs:
 * - tenant: Server tenant document returned by system-api.
 *
 * Outputs:
 * - Tenant document safe to write into Dexie without preserving stale lifecycle fields.
 *
 * Failure Handling:
 * - Invalid input is returned unchanged so API-shape issues surface through callers.
 *
 * Idempotency:
 * - REQUIRED.
 *
 * @localFirst
 *
 * Source of Truth:
 * - server
 *
 * Write Flow:
 * - Server reconciliation is normalized before updating the local Dexie source of truth.
 *
 * Sync Strategy:
 * - Prevents stale local lifecycle fields from surviving server reconciliation.
 *
 * Conflict Resolution:
 * - Server wins; an omitted provisioningError means the server has cleared it.
 */
function normalizeServerTenantForDexie<T extends Record<string, any> | null | undefined>(tenant: T): T {
  if (!tenant || typeof tenant !== 'object') return tenant;
  return {
    ...tenant,
    provisioningError: Object.prototype.hasOwnProperty.call(tenant, 'provisioningError')
      ? tenant.provisioningError
      : null,
  } as T;
}

/**
  * @contract TenantIdentityAvailabilityInput
  *
  * Purpose:
  * - Represents the tenant identity values that must be unique in local system Dexie.
  *
  * Fields:
  * - slug: Department URL slug generated from NERIS ID or name.
  * - nerisId: Optional NERIS fire department identifier.
  * - excludeTenantId: Optional tenant ID to ignore during edit flows.
  * - excludeDraftId: Optional onboarding draft ID to ignore during resumed provisioning.
  *
  * Rules:
  * - Slugs compare case-insensitively after lowercase normalization.
  * - NERIS IDs compare case-insensitively after uppercase normalization.
  *
  * Sync:
  * - Local-first preflight guard only; backend sync/provisioning remains the final authority.
  */
export interface TenantIdentityAvailabilityInput {
  slug?: string | null;
  nerisId?: string | null;
  excludeTenantId?: string | null;
  excludeDraftId?: string | null;
}

/**
  * @localFirst
  *
  * Source of Truth:
  * - local
  *
  * Write Flow:
  * - Reads and optimistic writes happen in Dexie first.
  *
  * Sync Strategy:
  * - Tenant lifecycle routes reconcile authoritative server state back into Dexie.
  *
  * Conflict Resolution:
  * - Server lifecycle routes win after optimistic local updates.
  */

/**
 * @process watchTenants
 *
 * Steps:
 * 1. Wrap a Dexie liveQuery around the non-deleted tenant table read.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - A Dexie Observable that emits the current visible tenant array whenever
 *   any row in the `tenants` table changes. Callers subscribe with
 *   `.subscribe({ next, error })` and unsubscribe on cleanup.
 *
 * Failure Handling:
 * - Observable error channel propagates Dexie read failures to the subscriber.
 *
 * Idempotency:
 * - REQUIRED — calling this multiple times is safe; each call returns an
 *   independent subscription.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Read-only reactive projection. No writes.
 *
 * Sync Strategy:
 * - Background replication writes into Dexie; liveQuery emits the change to
 *   all active subscribers automatically.
 *
 * Conflict Resolution:
 * - Caller receives the latest merged Dexie state.
 */
export function watchTenants(): Observable<any[]> {
  return liveQuery(async () => {
    const db = await getSystemDb();
    const docs = await db.tenants.toArray();
    return docs.filter((t: any) => !t._deleted);
  });
}

/**
 * @process refreshTenantFromServer
 *
 * Steps:
 * 1. Fetch the authoritative tenant record from GET /api/tenants/:id.
 * 2. Write the server document into Dexie, replacing the stale local copy.
 * 3. Return the refreshed record so callers can act on the latest state.
 *
 * Inputs:
 * - tenantId: 24-char hex tenant identifier.
 *
 * Outputs:
 * - The server-authoritative tenant record after it has been written to Dexie,
 *   or null if the record cannot be retrieved.
 *
 * Failure Handling:
 * - Network or API errors are surfaced to the caller. The existing Dexie
 *   record is not modified on failure.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly.
 *
 * @localFirst
 *
 * Source of Truth:
 * - server (for this reconciliation call only)
 *
 * Write Flow:
 * - Server response written directly into Dexie, bypassing optimistic logic.
 *   This is an explicit reconciliation, not an optimistic write.
 *
 * Sync Strategy:
 * - Intended for inherently-online flows (provisioning status checks) where
 *   waiting for the background replication pull is not acceptable.
 *
 * Conflict Resolution:
 * - Server wins. The authoritative document overwrites the local copy.
 */
export async function refreshTenantFromServer(tenantId: string): Promise<any | null> {
  const { api } = await import('../apiClient');
  const db = await getSystemDb();
  const fresh = await api.tenants.get(tenantId);
  if (fresh) {
    const normalized = normalizeServerTenantForDexie(fresh);
    await db.tenants.put(normalized);
    return normalized;
  }
  return fresh ?? null;
}

/**
 * @process listTenants
 *
 * Steps:
 * 1. Read all tenant rows from Dexie.
 * 2. Filter out locally deleted rows.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - All visible tenant rows from local storage.
 *
 * Failure Handling:
 * - Surfaces Dexie read failures to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function listTenants(): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.tenants.toArray();
  return docs.filter((t: any) => !t._deleted);
}

 /**
  * @process getTenant
  *
  * Steps:
  * 1. Read a tenant row from Dexie by id.
  * 2. Return null if the row does not exist or is locally deleted.
  *
  * Inputs:
  * - id: Tenant identifier.
  *
  * Outputs:
  * - A visible tenant row or null.
  *
  * Failure Handling:
  * - Surfaces Dexie read failures to the caller.
  *
  * Idempotency:
  * - REQUIRED.
  */
 export async function getTenant(id: string): Promise<any | null> {
  const db = await getSystemDb();
  const doc = await db.tenants.get(id);
  if (!doc) return null;
  return (doc as any)._deleted ? null : doc;
}

 /**
  * @process listTrials
  *
  * Steps:
  * 1. Read visible tenants from Dexie.
  * 2. Filter to trial-subscription departments.
  * 3. Compute transient UI trial fields.
  *
  * Inputs:
  * - None.
  *
  * Outputs:
  * - Trial tenant rows enriched with remaining-days metadata.
  *
  * Failure Handling:
  * - Surfaces underlying local read failures to the caller.
  *
  * Idempotency:
  * - REQUIRED.
  */
export async function listTrials(): Promise<any[]> {
  const all = await listTenants();
  const trials = all.filter(t => t.billing?.subscription?.status === 'trial' || t.trial === true);
  
  // Add computed trial fields
  const now = Date.now();
  return trials.map(t => {
    const endDate = t.trial?.endDate ? new Date(t.trial.endDate).getTime() : null;
    const trialExpired = endDate ? endDate < now : false;
    const trialDaysRemaining = endDate 
      ? Math.max(0, Math.ceil((endDate - now) / 86400000))
      : 0;
    
    return {
      ...t,
      _trialExpired: trialExpired,
      _trialDaysRemaining: trialDaysRemaining
    };
  });
}

/**
 * @process syncTrialsFromServer
 *
 * Steps:
 * 1. Fetch the authoritative tenant list from the server route.
 * 2. Write each server tenant row into Dexie.
 * 3. Return the locally-derived trial list.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - Recomputed local trial list after reconciliation.
 *
 * Failure Handling:
 * - Surfaces fetch or local write errors to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function syncTrialsFromServer(): Promise<any[]> {
  const { api } = await import('../apiClient');
  const db = await getSystemDb();
  
  // Fetch all tenants from server
  const serverTenants = await api.tenants.list({});
  
  // Update local cache with server data
  for (const tenant of serverTenants) {
    await db.tenants.put(tenant);
  }
  
  // Return filtered trials with computed fields
  return listTrials();
}

/**
  * @process assertTenantIdentityAvailableLocal
  *
  * Steps:
  * 1. Normalize the requested tenant slug and NERIS ID.
  * 2. Read current tenant rows from system Dexie.
  * 3. Reject duplicates before the UI creates or updates a local tenant record.
  *
  * Inputs:
  * - input: Tenant identity values to check.
  *
  * Outputs:
  * - void when the identity is locally available.
  *
  * Failure Handling:
  * - Throws a user-facing duplicate identity error when a local conflict exists.
  *
  * Idempotency:
  * - REQUIRED.
  *
  * @localFirst
  *
  * Source of Truth:
  * - local
  *
  * Write Flow:
  * - Read-only local validation; no mutation is performed.
  *
  * Sync Strategy:
  * - Normal sync keeps the tenant table current; backend guards still protect stale local state.
  *
  * Conflict Resolution:
  * - Local duplicate blocks UI progression; backend duplicate blocks final server persistence.
  */
export async function assertTenantIdentityAvailableLocal(input: TenantIdentityAvailabilityInput): Promise<void> {
  const db = await getSystemDb();
  const identity = normalizeTenantIdentity(input);
  const allTenants = await db.tenants.toArray();
  const conflict = allTenants.find((tenant: any) => {
    if (!tenant || tenant._deleted === true || tenant._id === input.excludeTenantId) return false;
    if (input.excludeDraftId && tenant.onboarding?.draftId === input.excludeDraftId) return false;
    const tenantIdentity = normalizeTenantIdentity(tenant);
    return Boolean(
      (identity.slug && tenantIdentity.slug === identity.slug)
      || (identity.nerisId && tenantIdentity.nerisId === identity.nerisId)
    );
  });
  if (conflict) {
    const conflictIdentity = normalizeTenantIdentity(conflict);
    const conflictField = identity.slug && conflictIdentity.slug === identity.slug ? 'slug' : 'nerisId';
    const conflictValue = conflictField === 'slug' ? identity.slug as string : identity.nerisId as string;
    throw new Error(buildTenantIdentityConflictMessage(conflict, conflictField, conflictValue));
  }
}

/**
 * @process getTenantByOnboardingDraftId
 *
 * Steps:
 * 1. Read local tenant rows from Dexie.
 * 2. Return the active tenant created by the matching onboarding draft.
 *
 * Inputs:
 * - draftId: Local-first onboarding draft ObjectId string.
 *
 * Outputs:
 * - Matching local tenant snapshot, or null.
 *
 * Failure Handling:
 * - Dexie read failures surface to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function getTenantByOnboardingDraftId(draftId: string): Promise<any | null> {
  const db = await getSystemDb();
  const allTenants = await db.tenants.toArray();
  const matches = allTenants.filter((tenant: any) =>
    tenant
    && tenant._deleted !== true
    && tenant.onboarding?.draftId === draftId
  );
  if (matches.length > 1) {
    throw new Error(`Multiple local tenants are linked to onboarding draft ${draftId}; cannot safely resume provisioning.`);
  }
  return matches[0] ?? null;
}

/**
 * @process deleteTenantLocalOnly
 *
 * Steps:
 * 1. Delete a local-only optimistic tenant row from Dexie.
 * 2. Do not wake sync because the canonical tenant row replaces this temporary id.
 *
 * Inputs:
 * - tenantId: Local temporary tenant id to remove.
 *
 * Outputs:
 * - void.
 *
 * Failure Handling:
 * - Dexie delete failures surface to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function deleteTenantLocalOnly(tenantId: string): Promise<void> {
  const db = await getSystemDb();
  await db.tenants.delete(tenantId);
}

 /**
  * @process upsertTenant
  *
  * Steps:
  * 1. Read the previous local tenant snapshot.
  * 2. Stamp a fresh updatedAt and write the new row into Dexie.
  * 3. Wake the local write pipeline.
  * 4. Record audit changes when applicable.
  *
  * Inputs:
  * - data: Tenant row to persist locally.
  *
  * Outputs:
  * - void.
  *
  * Concurrency:
  * - updatedAt is always refreshed so the optimistic-concurrency check in the
  *   server sync handler (executeSyncPush) accepts the push. Forwarding a stale
  *   updatedAt is rejected as a conflict and the edit never persists server-side.
  *
  * Failure Handling:
  * - Surfaces Dexie write failures to the caller.
  *
  * Idempotency:
  * - REQUIRED when the same payload is supplied.
  */
export async function upsertTenant(data: any): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.tenants.get(data._id);
  const isNew = !existing;
  const identity = normalizeTenantIdentity(data);
  await assertTenantIdentityAvailableLocal({
    ...identity,
    excludeTenantId: data._id,
    excludeDraftId: data.onboarding?.draftId,
  });

  const next = {
    ...data,
    _deleted: data._deleted === true,
    ...(identity.slug ? { slug: identity.slug } : {}),
    ...(identity.nerisId ? { nerisId: identity.nerisId } : {}),
    // Always stamp a fresh updatedAt on every tenant write. The server sync
    // handler (executeSyncPush) uses optimistic concurrency: a push whose
    // updatedAt is older than the server's current value is rejected as a stale
    // conflict and the client is told to self-correct, silently discarding the
    // edit. Callers (slide-out boundary edit, suspend/activate, inline edits)
    // forward the updatedAt the row was loaded with; preserving it loses the
    // server-side >= check so the change never persists. Stamping here keeps
    // every tenant write — onboarding and inline edit alike — consistent.
    updatedAt: new Date().toISOString(),
  };
  await db.tenants.put(next);
  wakeLocalWrite('tenants');

  const changes = isNew ? [] : computeAuditChanges(existing as Record<string, any>, next as Record<string, any>);
  if (isNew || changes.length > 0) {
    writeAuditLog({
      collection: 'tenants',
      documentId: data._id,
      action: isNew ? 'create' : 'update',
      summary: isNew ? `Created department "${next.name}"` : `Updated department "${next.name}"`,
      changes,
    }).catch((err) => {
      console.warn('[tenantService] Failed to write audit log for tenant upsert:', err);
    });
  }
}

 /**
  * @process deleteTenantLocal
  *
  * Steps:
  * 1. Read the local tenant row.
  * 2. Mark the row with `_deleted: true`.
  * 3. Wake the local write pipeline.
  *
  * Inputs:
  * - id: Tenant identifier.
  *
  * Outputs:
  * - void.
  *
  * Failure Handling:
  * - Surfaces Dexie write failures to the caller.
  *
  * Idempotency:
  * - REQUIRED.
  */
export async function deleteTenantLocal(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.tenants.get(id);
  if (!existing) return;
  const now = new Date().toISOString();
  await db.tenants.put({ ...(existing as any), _deleted: true, deletedAt: now, updatedAt: now });
  wakeLocalWrite('tenants');
}

/**
 * @process extendTenantTrialPeriod
 *
 * Steps:
 * 1. Read the tenant from Dexie.
 * 2. Optimistically extend the local trial end date and append extension history.
 * 3. Call the authoritative server-side trial extension route.
 * 4. Reconcile Dexie with the refreshed server copy when available.
 *
 * Inputs:
 * - tenantId: Department identifier.
 * - extensionDays: Number of days to add.
 * - reason: Optional operator note.
 *
 * Outputs:
 * - The best available tenant snapshot after local-first update and reconciliation.
 *
 * Failure Handling:
 * - Restores the previous Dexie snapshot if the server extension fails.
 *
 * Idempotency:
 * - NOT REQUIRED because each confirmed extension is a deliberate new lifecycle event.
 */
export async function extendTenantTrialPeriod(tenantId: string, extensionDays: number, reason?: string): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.tenants.get(tenantId);
  if (!existing) return null;

  const { api } = await import('../apiClient');
  const now = new Date().toISOString();
  const currentEndDate = (existing as any).trial?.endDate
    ? new Date((existing as any).trial.endDate).getTime()
    : Date.now();
  const nextEndDate = new Date(currentEndDate + extensionDays * 86400000).toISOString();
  const optimistic = {
    ...(existing as any),
    status: (existing as any).status === 'expired' ? 'trial' : (existing as any).status,
    billing: {
      ...((existing as any).billing ?? {}),
      subscription: {
        ...((existing as any).billing?.subscription ?? {}),
        status: 'trial',
      },
    },
    trial: {
      ...((existing as any).trial ?? {}),
      endDate: nextEndDate,
      extensions: [
        ...(((existing as any).trial?.extensions ?? []) as any[]),
        {
          days: extensionDays,
          reason: reason ?? '',
          addedAt: now,
        },
      ],
      outcome: null,
      outcomeAt: undefined,
    },
    updatedAt: now,
  };

  await db.tenants.put(optimistic);

  try {
    await api.tenants.trialExtend(tenantId, extensionDays, reason);
    const refreshed = await api.tenants.get(tenantId).catch(() => null);
    if (refreshed) {
      await db.tenants.put(refreshed);
      return refreshed;
    }
    return optimistic;
  } catch (error) {
    await db.tenants.put(existing as any);
    throw error;
  }
}

/**
 * @process convertTenantTrialToActive
 *
 * Steps:
 * 1. Read the tenant from Dexie.
 * 2. Optimistically clear the trial state locally so the department UI updates immediately.
 * 3. Call the authoritative server-side conversion route.
 * 4. Reconcile Dexie with the server copy when available.
 *
 * Inputs:
 * - tenantId: 24-char department id.
 * - notes: Optional operator note for the conversion route.
 *
 * Outputs:
 * - The best available active tenant snapshot after optimistic update and server reconciliation.
 *
 * Failure Handling:
 * - Restores the previous Dexie snapshot if the server conversion fails.
 *
 * Idempotency:
 * - REQUIRED — removing trial status should be safe to repeat.
 */
export async function convertTenantTrialToActive(tenantId: string, notes?: string): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.tenants.get(tenantId);
  if (!existing) return null;

  const { api } = await import('../apiClient');
  const now = new Date().toISOString();
  const optimistic = {
    ...(existing as any),
    status: 'active',
    billing: {
      ...((existing as any).billing ?? {}),
      subscription: {
        ...((existing as any).billing?.subscription ?? {}),
        status: 'active',
        startDate: (existing as any).billing?.subscription?.startDate ?? now,
        autoRenew: (existing as any).billing?.subscription?.autoRenew ?? true,
      },
    },
    trial: {
      ...((existing as any).trial ?? {}),
      outcome: 'converted',
      outcomeAt: now,
    },
    updatedAt: now,
  };

  await db.tenants.put(optimistic);

  try {
    await api.tenants.trialConvert(tenantId, notes);
    const refreshed = await api.tenants.get(tenantId).catch(() => null);
    if (refreshed) {
      await db.tenants.put(refreshed);
      return refreshed;
    }
    return optimistic;
  } catch (error) {
    await db.tenants.put(existing as any);
    throw error;
  }
}

/**
 * @process retryProvisioning
 *
 * Steps:
 * 1. POST /api/tenants/:id/retry-provisioning \u2014 server clears the
 *    provisioningError and re-runs runBackgroundProvisioning.
 * 2. Optimistically clear `provisioningError` on the local Dexie copy so the
 *    UI banner hides immediately while the background work runs. Sync will
 *    replicate the final status change (active / provisioningError) back.
 *
 * Inputs:
 * - tenantId: 24-char hex id.
 *
 * Outputs:
 * - void.
 *
 * Failure Handling:
 * - Surfaces API errors to the caller so the UI can show them.
 *
 * Idempotency:
 * - REQUIRED \u2014 server route is idempotent.
 */
export async function retryProvisioning(tenantId: string): Promise<void> {
  const { api } = await import('../apiClient');
  await api.tenants.retryProvisioning(tenantId);

  // Optimistic local clear so the banner disappears while the background
  // work runs. Sync push is not needed because the field on the server was
  // already unset by the retry endpoint.
  const db = await getSystemDb();
  const existing = await db.tenants.get(tenantId);
  if (existing) {
    await db.tenants.put({
      ...(existing as any),
      provisioningError: null,
      updatedAt: new Date().toISOString(),
    });
  }
}

/**
 * @process retryRegridImport
 *
 * Steps:
 * 1. POST /api/tenants/:id/retry-regrid-import — the server resolves the
 *    cache file, re-enqueues the regridIngestion job, and updates the
 *    `regrid_import` provisioning step to `running`.
 * 2. Optimistically update the local Dexie copy's `provisioningProgress.
 *    regrid_import` to `running` so the UI reflects the retry immediately
 *    while the background job runs.
 *
 * Inputs:
 * - tenantId: 24-char hex id.
 *
 * Outputs:
 * - void.
 *
 * Failure Handling:
 * - Surfaces API errors (including 409 when cache file is unavailable) to
 *   the caller so the UI can show them.
 *
 * Idempotency:
 * - REQUIRED — server route uses the same idempotency key as the initial
 *   onboarding enqueue (`regrid-onboarding-{tenantId}`).
 *
 * Source of Truth:
 * - server (re-enqueue is an inherently-online operation).
 *
 * Write Flow:
 * - Optimistic local update of provisioningProgress.regrid_import.
 *
 * Sync Strategy:
 * - Background sync will replicate the final job state back to Dexie.
 */
export async function retryRegridImport(tenantId: string): Promise<void> {
  const { api } = await import('../apiClient');
  await api.tenants.retryRegridImport(tenantId);

  // Optimistic local update so the UI shows "running" immediately.
  const db = await getSystemDb();
  const existing = await db.tenants.get(tenantId);
  if (existing) {
    const progress = {
      ...((existing as any).provisioningProgress ?? {}),
      regrid_import: { status: 'running', startedAt: new Date().toISOString() },
    };
    await db.tenants.put({
      ...(existing as any),
      provisioningProgress: progress,
      updatedAt: new Date().toISOString(),
    });
  }
}

/**
 * @process checkProvisioningStatus
 *
 * Steps:
 * 1. Call GET /api/tenants/:id/provisioning-status — the server actively
 *    verifies each provisioning step's real state (database exists, jobQueue
 *    jobs, admin user exists) rather than just re-reading the tenant document.
 * 2. If the server reconciled the tenant to active, re-fetch the tenant
 *    document and write it to Dexie so the liveQuery propagates the change.
 * 3. Return the structured per-step status report so the caller can show
 *    meaningful feedback.
 *
 * Inputs:
 * - tenantId: 24-char hex tenant identifier.
 *
 * Outputs:
 * - ProvisioningStatusReport with per-step verified state and reconciled flag.
 *
 * Failure Handling:
 * - Network or API errors are surfaced to the caller.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly. Server reconciliation is idempotent.
 *
 * Source of Truth:
 * - server (active verification of provisioning side effects).
 *
 * Write Flow:
 * - If reconciled, the updated tenant document is written to Dexie.
 *
 * Sync Strategy:
 * - Intended for the "Check Status" button — an inherently-online active
 *   verification that cannot be satisfied from local cache.
 *
 * Conflict Resolution:
 * - Server wins. If the server reconciled the tenant to active, the local
 *   copy is overwritten with the server-authoritative document.
 */
export async function checkProvisioningStatus(tenantId: string): Promise<{
  tenantId: string;
  tenantStatus: string;
  reconciled: boolean;
  steps: Record<string, { recorded?: string; actual: string; details?: string }>;
}> {
  const { api } = await import('../apiClient');
  const report = await api.tenants.provisioningStatus(tenantId);

  // If the server reconciled the tenant to active, refresh the local Dexie
  // copy so the liveQuery in TenantsPage propagates the status change and
  // the provisioning banner disappears.
  if (report?.reconciled) {
    await refreshTenantFromServer(tenantId);
  }

  return report;
}

/**
 * @process markTenantTrial
 * Purpose: Mark a tenant as trial via the server lifecycle endpoint and
 *          refresh the local Dexie copy.
 * Steps:
 * 1. Call api.tenants.trialMark on the server.
 * 2. Refresh the local Dexie copy from the server response.
 * Inputs:  tenantId, days (trial duration), reason (optional).
 * Outputs: void — local Dexie copy is refreshed.
 * Failure Handling: Throws on server failure; local cache is not modified.
 * Idempotency: NOT REQUIRED — marking trial is a one-time lifecycle action.
 */
export async function markTenantTrial(tenantId: string, days: number, reason?: string): Promise<void> {
  const { api } = await import('../apiClient');
  await api.tenants.trialMark(tenantId, days, reason);
  await refreshTenantFromServer(tenantId);
}

/**
 * @process cleanupTenantTrial
 * Purpose: Clean up (remove) trial status from a tenant via the server
 *          lifecycle endpoint and refresh the local Dexie copy.
 * Steps:
 * 1. Call api.tenants.trialCleanup on the server.
 * 2. Refresh the local Dexie copy from the server response.
 * Inputs:  tenantId.
 * Outputs: void — local Dexie copy is refreshed.
 * Failure Handling: Throws on server failure; local cache is not modified.
 * Idempotency: REQUIRED — cleaning up trial status should be safe to repeat.
 */
export async function cleanupTenantTrial(tenantId: string): Promise<void> {
  const { api } = await import('../apiClient');
  await api.tenants.trialCleanup(tenantId);
  await refreshTenantFromServer(tenantId);
}

/**
 * @process impersonateTenant
 * Purpose: Generate a system-entry impersonation redirect URL for entering
 *          a tenant's admin portal as the system operator.
 * Steps:
 * 1. Call api.systemEntry.impersonate on the server, passing the admin's
 *    current themeId so it carries over to tenant-web.
 * 2. Return the redirect URL to the caller.
 * Inputs:  tenantId, themeId (optional — the admin's current theme).
 * Outputs: { redirectUrl: string } — the URL to open in a new tab/popup.
 * Failure Handling: Throws on server failure.
 * Idempotency: NOT REQUIRED — each call generates a fresh entry token.
 */
export async function impersonateTenant(tenantId: string, themeId?: string): Promise<{ redirectUrl: string }> {
  const { api } = await import('../apiClient');
  return api.systemEntry.impersonate(tenantId, themeId);
}

/**
 * @process mobileImpersonateTenant
 * Purpose: Generate a system-entry mobile impersonation redirect URL for
 *          entering a tenant's mobile app as the system operator.
 * Steps:
 * 1. Call api.systemEntry.mobileImpersonate on the server, passing the
 *    admin's current themeId so it carries over to mobile-pwa.
 * 2. Return the redirect URL to the caller.
 * Inputs:  tenantId, themeId (optional — the admin's current theme).
 * Outputs: { redirectUrl: string } — the URL to open in a new tab/popup.
 * Failure Handling: Throws on server failure.
 * Idempotency: NOT REQUIRED — each call generates a fresh entry token.
 */
export async function mobileImpersonateTenant(tenantId: string, themeId?: string): Promise<{ redirectUrl: string }> {
  const { api } = await import('../apiClient');
  return api.systemEntry.mobileImpersonate(tenantId, themeId);
}
